import { Link } from "react-router-dom";
import { Mic, ShieldCheck, X } from "lucide-react";

// One-time explicit consent shown the first time a student taps the mic.
// Copy is deliberately narrow: SuperZeka does not intentionally store raw
// voice recordings in this native V1 path; the browser/OS may process the
// audio according to its own platform behaviour and privacy terms. This
// wording is required by the C1 review; do not soften it.
//
// Persistence model:
//   sz_voice_consent_v1 = "granted" -> mic is enabled going forward
//   sz_voice_consent_v1 = "denied"  -> mic remains blocked; revocation
//                                     surface for granted state lands in a
//                                     follow-up (Profile → privacy).
export function VoiceConsentCard({ open, onAccept, onDecline }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-consent-title"
      data-testid="voice-consent-card"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-zinc-200 overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-start gap-3">
          <div className="w-9 h-9 shrink-0 rounded-xl bg-[#1E3A8A]/5 border border-[#1E3A8A]/10 flex items-center justify-center">
            <Mic className="w-4 h-4 text-[#1E3A8A]" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="voice-consent-title"
              className="text-base font-semibold text-zinc-900"
              data-testid="voice-consent-title"
            >
              Sesli girişi kullanmak istiyor musun?
            </h2>
            <p className="mt-1 text-xs text-zinc-600 leading-relaxed">
              Mikrofonu yalnızca tuşa bastığında dinler; tek seferliktir.
            </p>
          </div>
          <button
            type="button"
            onClick={onDecline}
            className="text-zinc-400 hover:text-zinc-700 -mt-1"
            aria-label="Kapat"
            data-testid="voice-consent-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-4 space-y-3">
          <div
            className="flex gap-2 rounded-xl bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-xs text-zinc-700 leading-relaxed"
            data-testid="voice-consent-body"
          >
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" aria-hidden />
            <div>
              SuperZeka bu sürümde <strong>ham ses kaydını bilerek saklamaz</strong>;
              yalnızca konuşmandan çevrilen yazıyı sohbete gönderirsin.
              Ses tanıma tarayıcın veya cihazın tarafından yapılır; bu servisler
              sesi kendi platform davranışlarına ve gizlilik koşullarına göre
              işleyebilir. Daha fazlası için{" "}
              <Link
                to="/gizlilik"
                className="underline underline-offset-2 text-[#1E3A8A] hover:text-[#1E40AF]"
                data-testid="voice-consent-privacy-link"
              >
                Gizlilik Politikası
              </Link>
              .
            </div>
          </div>

          <ul className="text-[11.5px] text-zinc-600 space-y-1 pl-1">
            <li>• Mikrofon yalnızca butona bastığında ve tek seferliktir.</li>
            <li>• Yazıya çevrilen metni gönderme kararı sana aittir; istersen düzenlersin.</li>
            <li>• İzin vermezsen sesli girişi kapalı bırakırız; yazarak devam edebilirsin.</li>
          </ul>
        </div>

        <div className="px-5 py-4 border-t border-zinc-100 bg-zinc-50/60 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onDecline}
            className="h-9 px-4 rounded-full text-sm font-medium text-zinc-700 bg-white border border-zinc-200 hover:border-zinc-300"
            data-testid="voice-consent-decline"
          >
            Şimdi değil
          </button>
          <button
            type="button"
            onClick={onAccept}
            className="h-9 px-4 rounded-full text-sm font-medium text-white bg-[#1E3A8A] hover:bg-[#1E40AF]"
            data-testid="voice-consent-accept"
          >
            Sesli girişi aç
          </button>
        </div>
      </div>
    </div>
  );
}
